import { useState, useCallback, useRef, DragEvent } from 'react';
import { bibleState } from '@/state/bible-state';
import { UploadIcon, DownloadIcon, CheckCircleIcon, XCircleIcon } from '@components/icons';

interface PendingData {
  content: string;
  fileName: string;
  preview: { bookmarks: number; notes: number; prayerItems: number; history: number };
}

interface ImportResult {
  success: boolean;
  message: string;
  counts?: Record<string, number>;
}

/**
 * Import tab content - file drop zone and picker with confirmation
 */
export function ImportContent() {
  const [isDragging, setIsDragging] = useState(false);
  const [pendingData, setPendingData] = useState<PendingData | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Parse file and show preview (don't import yet)
  const handleFile = useCallback((file: File) => {
    if (!file.name.endsWith('.json')) {
      setImportResult({ success: false, message: 'Please select a JSON file' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      try {
        const data = JSON.parse(content);
        const preview = {
          bookmarks: Array.isArray(data.bookmarks) ? data.bookmarks.length : 0,
          notes: Array.isArray(data.notes) ? data.notes.length : 0,
          prayerItems: Array.isArray(data.prayerItems) ? data.prayerItems.length : 0,
          history: Array.isArray(data.bibleHistory) ? data.bibleHistory.length : 0,
        };
        setPendingData({ content, fileName: file.name, preview });
        setImportResult(null);
      } catch {
        setImportResult({ success: false, message: 'Invalid JSON file' });
      }
    };
    reader.onerror = () => {
      setImportResult({ success: false, message: 'Failed to read file' });
    };
    reader.readAsText(file);
  }, []);

  const confirmImport = useCallback(() => {
    if (!pendingData) return;
    const result = bibleState.importMobileData(pendingData.content);
    setImportResult(result);
    setPendingData(null);
  }, [pendingData]);

  const cancelImport = useCallback(() => {
    setPendingData(null);
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFile(files[0]);
      }
    },
    [handleFile],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFile(files[0]);
      }
      e.target.value = '';
    },
    [handleFile],
  );

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleExport = useCallback(() => {
    const jsonData = bibleState.exportUserData();
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `skjv-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 text-center">
        <h3 className="mb-1 text-lg font-semibold text-stone-800 dark:text-stone-200">
          Import / Export Data
        </h3>
        <p className="text-sm text-stone-500 dark:text-stone-400">
          Import or export your bookmarks, notes, prayers, and history
        </p>
      </div>

      {/* Drop zone */}
      <div
        className={`import-drop-zone ${isDragging ? 'dragging' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleFileSelect}
          className="hidden"
        />
        <UploadIcon className="mb-3 h-12 w-12 text-stone-400 dark:text-stone-500" />
        <p className="mb-1 text-lg font-medium text-stone-700 dark:text-stone-300">
          Tap to choose a file
        </p>
        <p className="text-sm text-stone-500 dark:text-stone-400">or drop a JSON file here</p>
      </div>

      {/* Export button */}
      <button
        onClick={handleExport}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-100/50 py-3 text-cyan-700 transition-all hover:bg-cyan-200/70 dark:bg-cyan-900/30 dark:text-cyan-300 dark:hover:bg-cyan-800/40"
      >
        <DownloadIcon className="h-5 w-5" />
        <span>Export Data</span>
      </button>

      {/* Pending import confirmation */}
      {pendingData && (
        <div className="mt-4 rounded-xl border border-amber-300/50 bg-amber-100/50 p-4 dark:border-amber-700/30 dark:bg-amber-900/20">
          <p className="mb-2 font-medium text-amber-800 dark:text-amber-200">
            Ready to import from {pendingData.fileName}
          </p>
          <ul className="mb-3 space-y-0.5 text-sm text-amber-700 dark:text-amber-300">
            {pendingData.preview.bookmarks > 0 && (
              <li>
                {pendingData.preview.bookmarks} bookmark
                {pendingData.preview.bookmarks !== 1 ? 's' : ''}
              </li>
            )}
            {pendingData.preview.notes > 0 && (
              <li>
                {pendingData.preview.notes} note{pendingData.preview.notes !== 1 ? 's' : ''}
              </li>
            )}
            {pendingData.preview.prayerItems > 0 && (
              <li>
                {pendingData.preview.prayerItems} prayer item
                {pendingData.preview.prayerItems !== 1 ? 's' : ''}
              </li>
            )}
            {pendingData.preview.history > 0 && (
              <li>
                {pendingData.preview.history} history entr
                {pendingData.preview.history !== 1 ? 'ies' : 'y'}
              </li>
            )}
          </ul>
          <p className="mb-3 text-xs text-amber-600 dark:text-amber-400">
            This will add to your existing data (duplicates will be skipped).
          </p>
          <div className="flex gap-2">
            <button
              onClick={cancelImport}
              className="flex-1 rounded-lg bg-stone-200/50 py-2 text-stone-600 transition-all hover:bg-stone-300/50 dark:bg-stone-700/50 dark:text-stone-400 dark:hover:bg-stone-600/50"
            >
              Cancel
            </button>
            <button
              onClick={confirmImport}
              className="flex-1 rounded-lg bg-amber-600 py-2 text-white transition-all hover:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-400"
            >
              Confirm Import
            </button>
          </div>
        </div>
      )}

      {/* Import result */}
      {importResult && (
        <div
          className={`mt-4 rounded-xl p-4 ${
            importResult.success
              ? 'border border-green-300/50 bg-green-100/50 dark:border-green-700/30 dark:bg-green-900/20'
              : 'border border-red-300/50 bg-red-100/50 dark:border-red-700/30 dark:bg-red-900/20'
          }`}
        >
          <div className="flex items-start gap-3">
            {importResult.success ? (
              <CheckCircleIcon className="h-6 w-6 flex-shrink-0 text-green-600 dark:text-green-400" />
            ) : (
              <XCircleIcon className="h-6 w-6 flex-shrink-0 text-red-600 dark:text-red-400" />
            )}
            <div>
              <p
                className={`font-medium ${
                  importResult.success
                    ? 'text-green-800 dark:text-green-200'
                    : 'text-red-800 dark:text-red-200'
                }`}
              >
                {importResult.success ? 'Import Successful!' : 'Import Failed'}
              </p>
              <p
                className={`mt-1 text-sm ${
                  importResult.success
                    ? 'text-green-700 dark:text-green-300'
                    : 'text-red-700 dark:text-red-300'
                }`}
              >
                {importResult.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-auto pt-4 text-xs text-stone-500 dark:text-stone-400">
        <p className="mb-1 font-medium">How to export from mobile:</p>
        <ol className="list-inside list-decimal space-y-0.5">
          <li>Open SKJV mobile app</li>
          <li>Go to Settings &gt; Backup</li>
          <li>Tap "Export Data"</li>
          <li>Save the JSON file</li>
        </ol>
      </div>
    </div>
  );
}
